import React,
{
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Button } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { BlockHeight } from '../../../../models/BlockSettings';
import CarouselItem from './CarouselItem';
import BlockCarouselItemData from '../BlockCarouselItemData';
import styles from './FadeCarousel.module.css';

interface FadeCarouselProps {
  carouselData: BlockCarouselItemData[];
  height: BlockHeight;
  isEditing: boolean;
  handleOpen: () => void;
  auto: boolean;
  interval: number;
  useFixedAspectRatio?: boolean;
  maxHeight: number;
}

const FadeCarousel: React.FC<FadeCarouselProps> = ({
  carouselData,
  height,
  isEditing,
  handleOpen,
  auto,
  interval,
  useFixedAspectRatio = false,
  maxHeight,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalIdRef = useRef<NodeJS.Timer | null>(null);

  const getPadding = () => {
    if (useFixedAspectRatio) {
      if (height === BlockHeight.TALL) {
        return '56.2%';
      }
      return '42.5%';
    }
    return undefined;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetAndStartInterval = () => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    setAutoSlideInterval();
  };

  const goToNextSlideFade = useCallback(() => {
    let nextIndex = currentIndex + 1;

    if (nextIndex > carouselData.length - 1) {
      nextIndex = 0;
    }

    setCurrentIndex(nextIndex);
    resetAndStartInterval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselData.length, currentIndex]);

  const setAutoSlideInterval = useCallback(() => {
    if (auto) {
      intervalIdRef.current = setInterval(() => {
        goToNextSlideFade();
      }, interval);
    }
  }, [auto, interval, goToNextSlideFade]);

  useEffect(() => {
    setAutoSlideInterval();

    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [setAutoSlideInterval]);

  const goToPreviousSlideFade = useCallback(() => {
    const previousIndex = currentIndex === 0 ? carouselData.length - 1 : currentIndex - 1;
    setCurrentIndex(previousIndex);
    resetAndStartInterval();
  }, [carouselData.length, currentIndex, resetAndStartInterval]);

  useEffect(() => {
    let id: any;

    if (auto) {
      id = setInterval(() => {
        goToNextSlideFade();
      }, interval);
    }

    return () => {
      if (id) clearInterval(id);
    };
  }, [auto, interval, carouselData.length, goToNextSlideFade]);

  return (
    <div
      id="blockFadeCarouselSlider"
      data-auto={auto}
      data-interval={interval}
      className={styles.fadeCarouselContainer}
    >
      <div
        id="blockFadeCarouselSlides"
        className={
          height === BlockHeight.TALL ? styles.fadeCarouselSlidesXl : styles.fadeCarouselSlides
        }
        onClick={goToNextSlideFade}
        style={{
          maxHeight: !useFixedAspectRatio ? maxHeight : undefined,
          paddingBottom: getPadding(),
        }}
      >
        {carouselData.map((item, index) => (
          <div
            className={`${styles.fadeSlide} ${index === currentIndex ? styles.activeSlide : ''}`}
            key={item.id}
          >
            <CarouselItem
              item={item}
              isEditing={isEditing}
              handleOpen={handleOpen}
              blockHeight={height}
              maxHeight={height === BlockHeight.TALL ? 650 : 350}
              useFixedAspectRatio={useFixedAspectRatio}
            />
          </div>
        ))}
      </div>
      <Button startIcon={<KeyboardArrowLeftIcon />} id="blockCarouselLeftNaviFade" type="button" onClick={goToPreviousSlideFade} className={styles.leftNaviButton} />
      <Button startIcon={<KeyboardArrowRightIcon />} id="blockCarouselRightNaviFade" type="button" onClick={goToNextSlideFade} className={styles.rightNaviButton} />
    </div>
  );
};

export default FadeCarousel;
